import type { LegalCitation } from '@buakei/types';
import {
  DataOriginBadge,
  ErrorState,
  LoadingState,
  PreviewAction,
  VerificationDimensions,
} from '@buakei/ui';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { demoLegalSourceService } from '@/services/legal-sources';

const KIND_LABEL = {
  statute: 'Statute',
  judgment: 'Judgment',
  rule: 'Rules',
  regulation: 'Regulation',
  other: 'Other',
} as const;

function Ids({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className="text-muted-600">{label}</dt>
      <dd className="font-mono text-xs leading-5 break-all text-ink-900">{value}</dd>
    </div>
  );
}

/** Legal Source Inspector (M1-03): IDs, passage text, provenance and per-dimension status. */
export function SourceInspector({ citation }: { citation: LegalCitation }) {
  const query = useQuery({
    queryKey: ['citation', citation.sourceId, citation.versionId, ...citation.passageIds],
    queryFn: () => demoLegalSourceService.resolveCitation(citation),
  });

  if (query.isPending) return <LoadingState label="Loading source" />;
  if (query.isError) {
    return (
      <ErrorState title="The source could not be loaded" onRetry={() => void query.refetch()}>
        Nothing is shown as evidence until the source loads.
      </ErrorState>
    );
  }

  const result = query.data;
  if (result.status === 'unresolved') {
    return (
      <div
        role="alert"
        className="flex flex-col gap-3 rounded-lg border border-danger-700/40 bg-white p-4"
      >
        <p className="flex items-center gap-2 font-semibold text-danger-700">
          <AlertTriangle className="size-4" aria-hidden /> Citation could not be resolved
        </p>
        <p className="text-sm text-ink-900">{result.reason}</p>
        <p className="text-sm text-ink-900">
          It is excluded from the evidence and is not shown as reviewed.
        </p>
        <dl className="grid gap-1 text-sm">
          <Ids label="Requested source" value={citation.sourceId} />
          <Ids label="Requested version" value={citation.versionId} />
          <Ids label="Requested passages" value={citation.passageIds.join(', ')} />
        </dl>
      </div>
    );
  }

  const { source, version, passages } = result;
  return (
    <article className="flex flex-col gap-5" aria-labelledby="inspector-source-title">
      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-baseline gap-x-4">
          <span className="text-xs text-muted-600">{KIND_LABEL[source.kind]}</span>
          <DataOriginBadge origin={source.dataOrigin} />
        </div>
        <h3 id="inspector-source-title" className="font-serif text-xl font-semibold text-navy-900">
          {source.title}
        </h3>
        <p className="text-sm text-muted-600">
          {[source.court, source.jurisdiction, source.citationAliases.join('; ')]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </header>

      <section aria-label="Cited passages" className="flex flex-col gap-3">
        {passages.map((p) => (
          <figure key={p.id} className="border-l-2 border-navy-900 py-1 pl-4">
            <figcaption className="mb-1 text-sm font-semibold text-navy-800">{p.label}</figcaption>
            <blockquote className="font-serif text-base leading-relaxed text-ink-900">
              {p.text}
            </blockquote>
          </figure>
        ))}
      </section>

      <section aria-labelledby="inspector-verification" className="flex flex-col gap-2">
        <h4 id="inspector-verification" className="text-sm font-semibold text-navy-900">
          Verification by dimension
        </h4>
        <VerificationDimensions verification={version.verification} />
      </section>

      <section aria-labelledby="inspector-ids" className="flex flex-col gap-2">
        <h4 id="inspector-ids" className="text-sm font-semibold text-navy-900">
          Provenance and identifiers
        </h4>
        <dl className="grid gap-1 text-sm">
          <Ids label="Source ID" value={source.id} />
          <Ids label="Version ID" value={`${version.id} (${version.versionLabel})`} />
          <Ids label="Passage IDs" value={passages.map((p) => p.id).join(', ')} />
          <Ids label="Content hash" value={version.contentHash} />
        </dl>
        <p className="text-sm text-muted-600">{source.provenance.rightsNote}</p>
      </section>

      <PreviewAction reason="Not in this demonstration. Report a problem from the document in the Library.">
        Report a problem with this source
      </PreviewAction>
    </article>
  );
}
